import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Dialog } from "@mui/material";
import { X } from "lucide-react";
import { toast } from "react-toastify";
import host from "../../global";
import { useTheme } from "../../hooks/useTheme";
import {
  markCompanyNameSet,
  registerCompanyProfileDialog,
} from "../../utils/companyProfileCheck";

// Settings opens on the Company Profile tab
const COMPANY_PROFILE_PATH = "/org-admin/configurations";

// What the "Back to ..." bar on Company Profile calls the page they came from
const RETURN_LABELS = [
  ["checkresume", "Screen Resume"],
  ["bulkresume", "Bulk Screening"],
  ["schedule-interview", "Screened Candidates"],
  ["jobs/", "Candidates"],
  ["activity", "Activity"],
  ["created-jds", "Jobs"],
  ["sourcing", "Sourcing"],
  ["sourced-candidates", "Sourced Candidates"],
];

function returnLabel(pathname) {
  const path = pathname.toLowerCase();
  return RETURN_LABELS.find(([segment]) => path.includes(segment))?.[1] || "where you left off";
}

function copyFor({ purpose, missingName, missingAbout }) {
  if (purpose === "invite") {
    return {
      title: "Before you schedule",
      // Above the name field
      lead: "Add your company name so your messages to candidates tell them who they're interviewing with.",
      effect: "Your company name isn't set, so candidates won't know who they're interviewing with.",
      fix: "add your company name",
      proceed: "Skip for now",
      recruiterProceed: "Send anyway",
    };
  }
  if (missingName && missingAbout) {
    return {
      title: "Complete your company profile",
      effect: "Applicants who open this link won't see your company name or anything about your company.",
      fix: "add your company name and About Company section",
      proceed: "Skip for now",
      recruiterProceed: "Create link anyway",
    };
  }
  if (missingName) {
    return {
      title: "Add your company name",
      effect: "Applicants who open this link won't see your company name.",
      fix: "add your company name",
      proceed: "Skip for now",
      recruiterProceed: "Create link anyway",
    };
  }
  return {
    title: "Add an About Company section",
    effect: "Applicants who open this link won't see anything about your company with the application form.",
    fix: "add a few lines about your company",
    proceed: "Skip for now",
    recruiterProceed: "Create link anyway",
  };
}

/** The dialog behind confirmCompanyDetails(). Mount once inside the router. */
export default function CompanyProfileCheckHost() {
  const navigate = useNavigate();
  const location = useLocation();
  const { theme } = useTheme();
  const dark = theme === "dark";
  const [request, setRequest] = useState(null);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const resolveRef = useRef(null);

  useEffect(() => registerCompanyProfileDialog(
    (req) => new Promise((resolve) => {
      resolveRef.current = resolve;
      setName("");
      setRequest(req);
    }),
  ), []);

  const finish = (goAhead) => {
    resolveRef.current?.(goAhead);
    resolveRef.current = null;
    setRequest(null);
  };

  if (!request) return null;
  const copy = copyFor(request);
  const { isAdmin, missingName, missingAbout } = request;
  // The name can be typed right here. About Company is too long for a pop-up,
  // so a link still missing it goes to Company Profile.
  const nameInline = isAdmin && missingName && !missingAbout;
  // Screen Resume keeps its results only on the page: leaving for Company
  // Profile would lose them, so there the name is asked for here only.
  const canLeave = !location.pathname.toLowerCase().includes("checkresume");

  const openCompanyProfile = () => {
    finish(false);
    navigate(COMPANY_PROFILE_PATH, {
      state: {
        returnTo: `${location.pathname}${location.search}`,
        returnLabel: returnLabel(location.pathname),
      },
    });
  };

  const saveName = async () => {
    const companyName = name.trim();
    if (!companyName || saving) return;
    setSaving(true);
    try {
      const token = sessionStorage.getItem("accessToken");
      await axios.put(
        `${host}/api/company-name`,
        { company_name: companyName },
        { headers: token ? { Authorization: `Bearer ${token}` } : {}, withCredentials: true },
      );
      markCompanyNameSet();
      finish(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't save the company name. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  let message;
  if (!isAdmin) message = `${copy.effect} Ask your admin to ${copy.fix} in Company Profile.`;
  else if (nameInline) message = copy.lead || copy.effect;
  else message = `${copy.effect} To give candidates the right information, ${copy.fix} in Company Profile.`;

  return (
    <Dialog
      open
      onClose={() => finish(false)}
      PaperProps={{
        sx: {
          background: dark
            ? "linear-gradient(233.57deg, rgba(34, 52, 97, 0.25) -5.01%, rgba(11, 10, 31, 0.05) 100%)"
            : "#FFFFFF",
          backdropFilter: "blur(50px)",
          WebkitBackdropFilter: "blur(50px)",
          borderRadius: "12px",
          maxWidth: "520px",
          width: "100%",
          boxShadow: "0 4px 20px rgba(0, 0, 0, 0.1)",
        },
      }}
      BackdropProps={{ sx: { backgroundColor: "rgba(0, 0, 0, 0.7)" } }}
    >
      {/* The setup guide treats this like the schedule pop-up it comes before */}
      <div className="relative p-8" data-guide="company-name-check">
        <button
          type="button"
          onClick={() => finish(false)}
          className="absolute top-7 right-7 p-1 hover:opacity-70 transition-opacity text-textSecondary dark:text-gray-400"
          title="Close"
        >
          <X size={20} />
        </button>

        <h2 className="text-xl font-medium pr-8 text-[#404040] dark:text-[#F9FAFB]">{copy.title}</h2>
        <p className="mt-2 text-sm leading-relaxed text-[#6B7280] dark:text-[#9CA3AF]">{message}</p>

        {nameInline && (
          <form
            className="mt-5"
            onSubmit={(e) => {
              e.preventDefault();
              saveName();
            }}
          >
            <label htmlFor="company-name-inline" className="block text-sm mb-1.5 text-[#404040] dark:text-[#D1D5DB]">
              Company name
            </label>
            <input
              id="company-name-inline"
              autoFocus
              value={name}
              maxLength={150}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Brightpath Staffing"
              className="w-full rounded-full px-4 py-2 text-sm outline-none border border-gray-300 bg-white text-[#111827] focus:border-blue-400 dark:bg-[#1E293B] dark:border-[#475569] dark:text-[#F9FAFB]"
            />
          </form>
        )}

        {/* Beside the name field only: without one, the main button already opens Company Profile */}
        {isAdmin && nameInline && canLeave && (
          <button
            type="button"
            onClick={openCompanyProfile}
            className="mt-3 text-sm font-medium bg-gradient-to-r from-pink-500 to-blue-500 bg-clip-text text-transparent hover:opacity-80"
          >
            Complete full company profile →
          </button>
        )}

        {/* No Cancel button: the X (or clicking outside) stops here */}
        <div className="mt-7 flex items-center justify-between gap-3">
          {isAdmin ? (
            <>
              <button
                type="button"
                onClick={() => finish(true)}
                className="text-sm text-[#6B7280] dark:text-[#9CA3AF] hover:underline"
              >
                {copy.proceed}
              </button>
              {nameInline ? (
                <button
                  type="button"
                  onClick={saveName}
                  disabled={!name.trim() || saving}
                  className="button px-6 py-2 rounded-full text-sm"
                >
                  <span>{saving ? "Saving..." : "Save & continue"}</span>
                </button>
              ) : (
                <button type="button" onClick={openCompanyProfile} className="button px-6 py-2 rounded-full text-sm">
                  <span>Complete company profile</span>
                </button>
              )}
            </>
          ) : (
            <button type="button" onClick={() => finish(true)} className="button px-6 py-2 rounded-full text-sm ml-auto">
              <span>{copy.recruiterProceed}</span>
            </button>
          )}
        </div>
      </div>
    </Dialog>
  );
}
